/**
 * Labstep
 *
 * @module screens/Master/Show
 * @desc Master Show Screen (deals with deleted)
 */

import ProtectedEntityLink from 'labstep-web/components/Layout/GlobalSearch/ProtectedEntityLink';
import { ErrorBoundaryContainer } from 'labstep-web/containers/ErrorBoundary';
import { MediaQueryDesktop as Desktop } from 'labstep-web/core/MediaQuery/Desktop';
import { SplitView } from 'labstep-web/core/SplitView';
import { Device } from 'labstep-web/models/device.model';
import type { Entity } from 'labstep-web/models/entity.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { CenterDeleted } from 'labstep-web/screens/Master/Show/CenterDeleted';
import { navigation, routing } from 'labstep-web/services/navigation';
import type { IRouting } from 'labstep-web/services/navigation.types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import styles from './styles.module.scss';
import type { IScreensMasterShowProps } from './types';

const SHOW_PAGE_SPLIT_VIEW_POSITION = 'showPageSplitViewPosition';

export const getTopLevelEntity = (entity: Entity) => {
  if (entity instanceof ResourceItem) {
    return entity.resource;
  }
  if (entity instanceof Protocol) {
    return entity.collection;
  }
  if (entity instanceof Experiment) {
    return entity.experiment_workflow;
  }
  return entity;
};

export class ScreensMasterShow extends React.Component<IScreensMasterShowProps> {
  componentDidUpdate(prevProps: IScreensMasterShowProps) {
    const { history, entity } = this.props;

    if (!prevProps.entity.deleted_at && entity.deleted_at) {
      let route = {
        to: `${entity.entityName}_index` as keyof IRouting,
        params: {},
      };
      if (routing[route.to]) {
        if (entity instanceof ResourceItem) {
          route = {
            to: 'resource_show' as const,
            params: { id: entity.resource.id },
          };
        }
        history.push(navigation.get(route.to, route.params));
      }
    }
  }

  render() {
    const { entity, center, noBorder, messageForDeleted, right } =
      this.props;

    if (entity.deleted_at) {
      return (
        <CenterDeleted
          entity={entity}
          messageForDeleted={messageForDeleted}
        />
      );
    }

    if (noBorder) {
      return center;
    }

    const left = (
      <div className={styles.center} id="screens-master-show-center">
        {center}
      </div>
    );

    const storedInitialWidth = localStorage.getItem(
      SHOW_PAGE_SPLIT_VIEW_POSITION,
    );
    const initialWidth = storedInitialWidth
      ? parseInt(storedInitialWidth, 10)
      : 10000;

    const onChange = (x: number) =>
      localStorage.setItem(SHOW_PAGE_SPLIT_VIEW_POSITION, String(x));

    return (
      <Desktop>
        {({ match }) => (
          <div className={styles.container}>
            {match ? (
              <SplitView
                left={left}
                right={right}
                initialWidth={initialWidth}
                minLeftWidth={500}
                onChange={onChange}
              />
            ) : (
              <>
                {left}
                {right}
              </>
            )}
            <ErrorBoundaryContainer FallbackComponent={<div />}>
              {(entity instanceof Device ||
                entity instanceof OrderRequest ||
                entity instanceof PurchaseOrder ||
                entity instanceof Resource ||
                entity instanceof ResourceItem) && (
                <ProtectedEntityLink
                  // Remount on entity change
                  key={`${entity.entityName}_${entity.id}`}
                  entity={entity}
                />
              )}
            </ErrorBoundaryContainer>
          </div>
        )}
      </Desktop>
    );
  }
}

export default withRouter(ScreensMasterShow);
